import { Component, inject, input, signal } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

import type { KlConfirmTone } from '@core/services/kl-confirm-dialog.service';

/**
 * Confirmation content for PrimeNG `DialogService`.
 * Prefer opening via `KlConfirmDialogService` (`@core/services/kl-confirm-dialog.service`).
 *
 * `title`, `description`, `confirmLabel`, and `cancelLabel` are passed through the translate pipe
 * (i18n keys). Use plain text only if keys exist or you accept the raw string as fallback.
 */
@Component({
  selector: 'kl-confirm-dialog',
  standalone: false,
  templateUrl: './kl-confirm-dialog.html',
  styleUrl: './kl-confirm-dialog.scss',
})
export class KlConfirmDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);

  /** i18n key (or literal) for the heading. */
  readonly title = input('');
  /** i18n key (or literal) for body text; omit or empty to hide. */
  readonly description = input('');
  /** i18n key for the primary button. */
  readonly confirmLabel = input('yes');
  /** i18n key for the secondary button. */
  readonly cancelLabel = input('no');

  /** Phosphor icon classes, e.g. `ph ph-warning`, `ph ph-trash`. */
  readonly icon = input('ph ph-warning');

  readonly confirmSeverity = input<
    'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'help' | 'danger' | 'contrast'
  >('danger');
  readonly cancelSeverity = input<
    'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'help' | 'danger' | 'contrast'
  >('secondary');

  /** Visual kind, exposed as `data-tone` for styling. */
  readonly tone = input<KlConfirmTone>('danger');

  readonly confirmLoading = input(false);

  /** When true, only the primary action is shown (e.g. informational ack dialogs). */
  readonly hideCancel = input(false);

  /** Set via `openWithCheckbox()` - closes with `{ confirmed, checked }` instead of a boolean. */
  readonly checkboxLabel = input<string | null>(null);
  readonly checked = signal(false);

  onConfirmClick(): void {
    this.dialogRef.close(this.checkboxLabel() ? { confirmed: true, checked: this.checked() } : true);
  }

  onCancelClick(): void {
    this.dialogRef.close(this.checkboxLabel() ? { confirmed: false, checked: this.checked() } : false);
  }
}
